export interface DiceRollResult {
  expression: string;
  rolls: number[];
  modifier: number;
  total: number;
  breakdown: string;
}

type KeepMode = "highest" | "lowest";

type ParsedTerm =
  | {
      kind: "modifier";
      sign: 1 | -1;
      value: number;
    }
  | {
      kind: "dice";
      sign: 1 | -1;
      count: number;
      faces: number;
      keepMode?: KeepMode;
      keepCount?: number;
      kept: number[];
      dropped: number[];
      subtotal: number;
    };

function isDigit(char: string) {
  return char >= "0" && char <= "9";
}

function parseIntAt(input: string, start: number) {
  let end = start;
  while (end < input.length && isDigit(input[end])) {
    end += 1;
  }

  if (end === start) return null;
  return { value: Number(input.slice(start, end)), end };
}

function pickRandomFace(faceCount: number): number {
  const bucket = new Uint32Array(1);
  const threshold = Math.floor(0x1_0000_0000 / faceCount) * faceCount;
  let raw: number;

  do {
    crypto.getRandomValues(bucket);
    raw = bucket[0];
  } while (raw >= threshold);

  return (raw % faceCount) + 1;
}

function parseKeepToken(input: string, start: number) {
  const marker = input.slice(start, start + 2);
  if (marker !== "kh" && marker !== "kl") return null;
  const keepMode: KeepMode = marker === "kh" ? "highest" : "lowest";
  const parsedCount = parseIntAt(input, start + 2);
  if (!parsedCount) {
    throw new Error(`Missing keep count in keep modifier at index ${start}`);
  }

  return {
    keepMode,
    keepCount: parsedCount.value,
    end: parsedCount.end,
  };
}

function parseDiceTerm(
  input: string,
  index: number,
  sign: 1 | -1,
): {
  cursor: number;
  term: ParsedTerm;
} {
  let cursor = index;
  let rawCount = 1;

  if (isDigit(input[cursor])) {
    const parsedCount = parseIntAt(input, cursor);
    if (!parsedCount) {
      throw new Error(`Expected dice count at index ${cursor}`);
    }

    rawCount = parsedCount.value;
    cursor = parsedCount.end;
  } else if (input[cursor] !== "d") {
    throw new Error(`Expected dice expression at index ${cursor}`);
  }

  if (input[cursor] !== "d") {
    throw new Error(`Missing "d" in dice expression at index ${cursor}`);
  }
  cursor += 1;

  const parsedFaces = parseIntAt(input, cursor);
  if (!parsedFaces) {
    throw new Error(`Missing die faces in dice expression at index ${cursor}`);
  }
  cursor = parsedFaces.end;

  const count = Math.max(1, Math.floor(rawCount));
  const faces = Math.max(1, Math.floor(parsedFaces.value));
  if (!Number.isInteger(count) || count < 1) {
    throw new Error(`Invalid dice count at index ${index}`);
  }
  if (!Number.isInteger(faces) || faces < 1) {
    throw new Error(`Invalid die faces at index ${index}`);
  }

  let keepMode: KeepMode | undefined;
  let keepCount: number | undefined;

  const keep = parseKeepToken(input, cursor);
  if (keep) {
    keepMode = keep.keepMode;
    keepCount = keep.keepCount;
    cursor = keep.end;
  }

  if (
    keepCount !== undefined &&
    (!Number.isInteger(keepCount) || keepCount < 1)
  ) {
    throw new Error(`Invalid keep count at index ${cursor}`);
  }
  if (keepCount !== undefined && keepCount > count) {
    throw new Error(`Keep count cannot exceed dice count at index ${cursor}`);
  }

  const rolls: number[] = [];
  for (let i = 0; i < count; i += 1) {
    rolls.push(pickRandomFace(faces));
  }

  const sortedRolls = [...rolls].sort((a, b) => a - b);
  let kept: number[] = rolls;
  let dropped: number[] = [];

  if (keepMode && keepCount) {
    if (keepMode === "highest") {
      kept = sortedRolls.slice(sortedRolls.length - keepCount);
      dropped = sortedRolls.slice(0, sortedRolls.length - keepCount);
    } else {
      kept = sortedRolls.slice(0, keepCount);
      dropped = sortedRolls.slice(keepCount);
    }
  }

  const subtotal = kept.reduce((acc, value) => acc + value, 0) * sign;

  return {
    cursor,
    term: {
      kind: "dice" as const,
      sign,
      count,
      faces,
      keepMode,
      keepCount,
      kept,
      dropped,
      subtotal,
    },
  };
}

function parseModifierTerm(
  input: string,
  index: number,
  sign: 1 | -1,
): {
  cursor: number;
  term: ParsedTerm;
} {
  const parsed = parseIntAt(input, index);
  if (!parsed) {
    throw new Error(`Expected numeric term at index ${index}`);
  }
  return {
    cursor: parsed.end,
    term: {
      kind: "modifier" as const,
      sign,
      value: parsed.value * sign,
    },
  };
}

function formatDiceTerm(term: ParsedTerm, isFirstTerm: boolean) {
  if (term.kind !== "dice") return "";

  const keepSuffix =
    term.keepMode && term.keepCount
      ? `${term.keepMode === "highest" ? "kh" : "kl"}${term.keepCount}`
      : "";
  const prefix =
    isFirstTerm && term.sign > 0 ? "" : term.sign < 0 ? " - " : " + ";
  const keptLabel = `[${term.kept.join(", ")}]`;
  const droppedLabel =
    term.dropped.length > 0 ? ` (drop ${term.dropped.join(", ")})` : "";
  return `${prefix}${term.count}d${term.faces}${keepSuffix} ${keptLabel}${droppedLabel}`;
}

function formatModifierTerm(term: ParsedTerm, isFirstTerm: boolean) {
  if (term.kind !== "modifier") return "";
  const signed = term.value >= 0 ? `+${term.value}` : `${term.value}`;
  return isFirstTerm ? `${signed}` : ` ${signed}`;
}

export function parseDiceExpression(input: string): DiceRollResult {
  const expression = input.trim().toLowerCase().replace(/\s+/g, "");
  if (!expression) {
    throw new Error("Expression is empty.");
  }

  const terms: ParsedTerm[] = [];
  let cursor = 0;
  let modifier = 0;
  const breakdownParts: string[] = [];
  const flattenedRolls: number[] = [];

  while (cursor < expression.length) {
    let sign: 1 | -1 = 1;

    if (expression[cursor] === "+") {
      sign = 1;
      cursor += 1;
    } else if (expression[cursor] === "-") {
      sign = -1;
      cursor += 1;
    }

    if (cursor >= expression.length) {
      throw new Error(`Unexpected operator at index ${cursor - 1}`);
    }

    const firstChar = expression[cursor];
    const isDiceStart = firstChar === "d" || isDigit(firstChar);
    const nextTerm = isDiceStart
      ? parseDiceTerm(expression, cursor, sign)
      : parseModifierTerm(expression, cursor, sign);

    cursor = nextTerm.cursor;
    terms.push(nextTerm.term);

    const term = nextTerm.term;
    const isFirstTerm = terms.length === 1;
    if (term.kind === "modifier") {
      modifier += term.value;
      breakdownParts.push(formatModifierTerm(term, isFirstTerm));
    } else {
      flattenedRolls.push(...term.kept);
      breakdownParts.push(formatDiceTerm(term, isFirstTerm));
    }

    if (cursor >= expression.length) break;

    const operator = expression[cursor];
    if (operator !== "+" && operator !== "-") {
      throw new Error(
        `Unexpected token "${expression[cursor]}" at index ${cursor}`,
      );
    }
  }

  const subtotal = terms.reduce((acc, term) => {
    if (term.kind === "modifier") return acc;
    return acc + term.subtotal;
  }, 0);

  const total = subtotal + modifier;
  const breakdown = `${breakdownParts.join("")} = ${total}`;

  return {
    expression,
    rolls: flattenedRolls,
    modifier,
    total,
    breakdown,
  };
}
